import { ActionIcon, Card } from '@mantine/core'
import { IconCheck, IconShoppingBagPlus } from '@tabler/icons-react'
import clsx from 'clsx'
import { useState } from 'react'

import { ProductCardPrice } from 'src/components/ProductCard/ProductCardPrice'
import { ShopifyImage } from 'src/components/ShopifyImage/ShopifyImage'
import { Text } from 'src/components/Text/Text'
import { type ProductVariant } from 'src/data/graphql-generated'
import { addToCartAction } from 'src/functions/cart/addToCart'
import { removeBioOrganic } from 'src/functions/product/removeBioOrganic'
import { getMetafield } from 'src/functions/util/getMetafield'
import { useCartLinesAddMutation } from 'src/hooks/useCartLinesAddMutation'
import { useCartQuery } from 'src/hooks/useCartQuery'
import colorVariables from 'src/styles/Colors.module.scss'

import styles from './MatchingProductCard.module.scss'

interface MatchingProductCardProps {
  variant: ProductVariant
}

export const MatchingProductCard = ({ variant }: MatchingProductCardProps) => {
  const { data: cart } = useCartQuery()
  const { mutateAsync: addToCart, isPending: isAddToCartBusy } = useCartLinesAddMutation()
  const [showCheck, setShowCheck] = useState(false)

  const {
    title: variantTitle,
    metafields,
    image,
    product: { title: productTitle },
  } = variant

  const variantArchived = getMetafield(metafields || [], 'product', 'archived')
  if (variantArchived) return null

  const productTitleWithoutBio = removeBioOrganic(productTitle)
  const checkedVariantTitle = variantTitle === 'Default Title' ? '' : variantTitle

  const onAddToCart = async () => {
    await addToCartAction({
      product: variant.product,
      variant,
      quantity: 1,
      addToCartMutationFn: addToCart,
      cartId: cart?.id,
    })
    setShowCheck(true)
  }

  return (
    <Card classNames={{ root: clsx(styles.card, showCheck && styles.checked) }} onClick={onAddToCart}>
      <ShopifyImage
        image={image}
        width={40}
        height={40}
        alt={productTitleWithoutBio}
        title={productTitleWithoutBio}
        lazy
        className={styles.image}
      />
      <div className={styles.textContent}>
        <div>
          <Text variant="p" bold className={styles.title} title={productTitleWithoutBio}>
            {productTitleWithoutBio}
          </Text>
          <Text small className={styles.variantTitle}>
            {checkedVariantTitle}
          </Text>
        </div>
        <ProductCardPrice productVariant={variant} isAlternative hideCompareAtPrice />
      </div>
      <ActionIcon
        variant="transparent"
        color={showCheck ? colorVariables['color-rainforest-primary'] : colorVariables['color-indigo-primary']}
        size="xl"
        className={styles.actionIcon}
        loading={isAddToCartBusy}
        onClick={onAddToCart}
      >
        {showCheck ? <IconCheck /> : <IconShoppingBagPlus />}
      </ActionIcon>
    </Card>
  )
}
